/**
 * Ilova qobig'i va marshrutlar — PDP Design System (design.pdp.uz).
 *
 * «Sahifa = papka» qoidasi (CLAUDE.md 7): har sahifa `src/pages/<sahifa>/` da.
 * Interfeys matnlari o'zbek tilida (NF-601/602).
 *
 * QOBIQ TUZILISHI — design.pdp.uz «suzuvchi canvas» naqshi (DS breakpointlari):
 *   - ≥1024 (`lg`): yon panel butun ilova bilan BIRGA `flex` qatorda,
 *     14px bo'shliqda (`gap-3.5`), 14px chekka bo'shliq bilan (`p-3.5`),
 *     `sticky` va `rounded-panel` (24px) — endi `fixed`/edge-to-edge EMAS;
 *     1024–1439 (`lg`) da tor (84px, faqat ikon), 1440+ (`xl`) da 272px.
 *   - ≤1023: yuqori panel + overlay menyu (to'liq 272px navy panel) —
 *     mobil UX ehtiyoji bilan saqlangan, lekin shakl bir xil (gradient,
 *     rounded-panel, ko'k soya).
 * Yon panel foni — 3 nuqtali gradient (`bg-navy-grad`, DS.gradient.navy).
 * Aktiv nav bandi: bg-white/10 + chap tomonda 3px sariq indikator.
 *
 * SESSIYA BIR MARTA o'qiladi va kontekst orqali tarqatiladi (`app/session.tsx`).
 */

import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import type { FormEvent, JSX, ReactNode } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { LogOut, Menu, Search } from "lucide-react";

import { api, getAccessToken, setTokens } from "./api/client";
import type { SessionUser } from "./api/client";
import { visibleGroups } from "./app/nav";
import { NotificationBell } from "./app/NotificationBell";
import { SessionProvider } from "./app/session";
import LoginPage from "./pages/login/LoginPage";

import { ROLE_LABEL } from "./theme/tokens";
import { Icon, PulseMark, SpinnerIcon } from "./ui/icons";
import { Skeleton, cx } from "./ui/primitives";

/**
 * Sahifalar TALAB BO'YICHA yuklanadi.
 *
 * Sabab o'lchangan: ECharts butun bundlning ~60 % ini tashkil qiladi
 * (840 kb → 273 kb gzip). Kirish sahifasiga diagramma kutubxonasini
 * yuklash — birinchi ochilishni sekinlashtiradi va u NF-201 (javob vaqti)
 * bo'yicha eng ko'p sezilib turadigan joy.
 *
 * `LoginPage` ATAYLAB oddiy import: u birinchi ekran va uni kechiktirish
 * «oq sahifa» effektini beradi.
 */
const AnalyticsPage = lazy(() => import("./pages/analytics/AnalyticsPage"));
const AssistantPage = lazy(() => import("./pages/assistant/AssistantPage"));
const CallPage = lazy(() => import("./pages/call/CallPage"));
const CallsPage = lazy(() => import("./pages/calls/CallsPage"));
const LeadDetailPage = lazy(() => import("./pages/leads/LeadDetailPage"));
const LeadsPage = lazy(() => import("./pages/leads/LeadsPage"));
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage"));
const NotificationsPage = lazy(() => import("./pages/notifications/NotificationsPage"));
const ProfilePage = lazy(() => import("./pages/profile/ProfilePage"));
const ReportsPage = lazy(() => import("./pages/reports/ReportsPage"));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage"));
const SignalsPage = lazy(() => import("./pages/signals/SignalsPage"));
const TeamPage = lazy(() => import("./pages/team/TeamPage"));
const TasksPage = lazy(() => import("./pages/tasks/TasksPage"));
const MyPage = lazy(() => import("./pages/my/MyPage"));
// PP-412 — LAYOUTSIZ, login talab qilmaydi (token URL da).
const SharedCallPage = lazy(() => import("./pages/shared/SharedCallPage"));
const AcceptInvitePage = lazy(() => import("./pages/accept-invite/AcceptInvitePage"));
// NF-505 — ochiq status sahifa: layoutsiz va authsiz.
const StatusPage = lazy(() => import("./pages/status/StatusPage"));

/** Ilova versiyasi — `vite.config.ts` dagi `define` orqali package.json dan. */
export const APP_VERSION = __APP_VERSION__;

/** Butun sahifani egallaydigan yuklanish holati — faqat sessiya tekshiruvi uchun. */
function BootScreen(): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-canvas">
      <PulseMark className="size-10" />
      <div className="flex items-center gap-2 text-[15px] text-tertiary">
        <SpinnerIcon className="size-4" />
        Yuklanmoqda…
      </div>
    </div>
  );
}

/**
 * Yon paneldagi bitta havola — navy fonda: aktiv band bg-white/10 va
 * chapdan 3px sariq indikator; radius-16 (DS «menyu bandi»).
 */
function NavLink({
  to,
  label,
  icon,
  active,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: Parameters<typeof Icon>[0]["name"];
  active: boolean;
  onNavigate: () => void;
}): JSX.Element {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      title={label}
      className={cx(
        "relative flex min-h-11 items-center gap-3 rounded-tile px-3.5 py-2.5 text-[15px] font-semibold transition-colors",
        "lg:justify-center lg:px-0 xl:justify-start xl:px-3.5",
        // Nofaol matn — DS manba qiymati #A9B5DC ga yaqin token (brand-200).
        active ? "bg-white/10 text-white" : "text-brand-200 hover:bg-white/5 hover:text-white",
      )}
    >
      {/* Aktiv indikator — 3px sariq chiziq (DS yellow aksenti). */}
      {active ? (
        <span className="absolute inset-y-2 left-0 w-[3px] rounded-full bg-yellow" aria-hidden />
      ) : null}
      <Icon
        name={icon}
        className={cx("size-5 shrink-0", active ? "text-yellow" : "text-white/55")}
      />
      {/* 1024 da faqat ikon (lg), 1440 dan matn qaytadi (xl). */}
      <span className="lg:hidden xl:inline">{label}</span>
    </Link>
  );
}

/** Kirish talab qiladigan qobiq. */
function Shell({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [checked, setChecked] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarQuery, setSidebarQuery] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function check(): Promise<void> {
      if (getAccessToken() === null) {
        if (!cancelled) {
          setChecked(true);
        }
        return;
      }

      try {
        const me = await api.me();
        if (!cancelled) {
          setUser(me);
        }
      } catch {
        // Token eskirgan va yangilab bo'lmadi — kirish sahifasiga qaytariladi.
        setTokens(null);
      } finally {
        if (!cancelled) {
          setChecked(true);
        }
      }
    }

    void check();

    return () => {
      cancelled = true;
    };
  }, []);

  const switchBusiness = useCallback(async (businessId: string): Promise<void> => {
    const result = await api.switchBusiness(businessId);
    setTokens(result.accessToken);
    // Barcha sahifalar yangi tenant konteksti bilan qayta yuklanadi.
    // `reload` — eng ishonchli yo'l: har sahifadagi keshni qo'lda tozalash
    // bitta joyni unutish bilan tugaydi va foydalanuvchi BOSHQA biznesning
    // ma'lumotini ko'radi.
    window.location.assign("/");
  }, []);

  const logout = useCallback((): void => {
    void api.logout().catch(() => undefined);
    setTokens(null);
    navigate("/login");
  }, [navigate]);

  /** Yon paneldagi tezkor qidiruv — CallsPage'ning `q` filtriga yo'naltiradi. */
  const submitSidebarSearch = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      const trimmed = sidebarQuery.trim();
      if (trimmed.length < 2) {
        return;
      }
      setMenuOpen(false);
      navigate(`/calls?q=${encodeURIComponent(trimmed)}`);
    },
    [navigate, sidebarQuery],
  );

  if (!checked) {
    return <BootScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const groups = visibleGroups(user.role);
  const isActive = (to: string, exact?: boolean): boolean =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  return (
    <SessionProvider value={{ user, switchBusiness, logout }}>
      {/* ── Suzuvchi canvas: sahifa foni + 14px chekka bo'shliq, yon panel
          va asosiy ustun BIR flex qatorda, 14px gap (design.pdp.uz naqshi).
          ≤1023 da yon panel mobil overlay bo'lib qoladi (shakli saqlangan). */}
      <div className="min-h-screen bg-canvas lg:flex lg:items-start lg:gap-3.5 lg:p-3.5">
        {/* ── Navy (gradient) yon panel: mobil overlay 272px · lg 84px · xl 272px ── */}
        <aside
          className={cx(
            "fixed inset-y-3.5 left-3.5 z-40 flex w-[272px] flex-col overflow-hidden",
            "rounded-panel bg-navy-grad shadow-navy transition-transform",
            // `lg:inset-y-auto` mobil `inset-y-3.5` balandlik chegarasini
            // (top+bottom) bekor qiladi — shuning uchun aynan shu yerda
            // ANIQ balandlik qo'yiladi (100vh − 2×14px chekka), aks holda
            // sidebar tabiiy kontent balandligigacha o'sadi va past
            // ekranlarda (mas. 1366×768 noutbuk) pastki profil bloki
            // ko'rinmay qoladi (o'lchangan xato — CDP orqali tasdiqlangan).
            "lg:sticky lg:inset-y-auto lg:left-auto lg:top-3.5 lg:h-[calc(100vh-1.75rem)] lg:w-[84px] lg:shrink-0 lg:translate-x-0 lg:self-start",
            "xl:w-[272px]",
            menuOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <div className="flex flex-col gap-[9px] px-4 pt-5">
            <div className="flex items-center gap-3 lg:justify-center xl:justify-start">
              <PulseMark className="size-8 shrink-0" />
              <div className="min-w-0 lg:hidden xl:block">
                <div className="text-[15px] font-extrabold leading-tight text-white">PDP Pulse</div>
                <div className="truncate text-[12.5px] font-semibold text-brand-200">
                  AI Sales Intelligence
                </div>
              </div>
            </div>
          </div>

          {/* Tezkor qidiruv — CallsPage transkript qidiruviga yo'naltiradi. */}
          <form onSubmit={submitSidebarSearch} className="mt-5 px-3 lg:hidden xl:block">
            <label className="flex h-11 items-center gap-2.5 rounded-tile border border-white/10 bg-white/[0.09] px-3.5">
              <Search
                className="size-[17px] shrink-0 text-white/55"
                strokeWidth={1.9}
                aria-hidden="true"
              />
              <input
                value={sidebarQuery}
                onChange={(event) => {
                  setSidebarQuery(event.target.value);
                }}
                placeholder="Qidiruv…"
                aria-label="Qo'ng'iroqlarni qidirish"
                className="min-w-0 flex-1 border-none bg-transparent text-[14px] font-medium text-white outline-none placeholder:text-white/45"
              />
            </label>
          </form>

          {/* `min-h-0` — flex ustunda overflow-y-auto haqiqiy ishlashi uchun
              majburiy (flex bolasi standart bo'yicha `min-height:auto`,
              bu overflow'ni ishlatmasdan ota-onani cho'zib yuboradi). */}
          <nav className="mt-[22px] min-h-0 flex-1 overflow-y-auto px-3 pb-4 lg:px-3.5 xl:px-3">
            {groups.map((group, index) => (
              <div
                key={group.kicker ?? `group-${String(index)}`}
                className={index > 0 ? "mt-6" : ""}
              >
                {group.kicker ? (
                  <div className="px-3.5 pb-1 text-[11px] font-extrabold uppercase tracking-kicker text-brand-200 lg:hidden xl:block">
                    {group.kicker}
                  </div>
                ) : null}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      label={item.label}
                      icon={item.icon}
                      active={isActive(item.to, item.exact)}
                      onNavigate={() => {
                        setMenuOpen(false);
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Pastdagi foydalanuvchi bloki — rounded-tile «karta» og'irligi
              bilan. `shrink-0` — nav skroll qilganda bu blok siqilib
              qolmasligi kerak, doim to'liq balandlikda ko'rinsin. */}
          <div className="shrink-0 px-3 pb-3 lg:px-2 xl:px-3">
            <Link
              to="/profile"
              onClick={() => {
                setMenuOpen(false);
              }}
              className="flex items-center gap-3 rounded-tile bg-white/5 px-2.5 py-2.5 transition-colors hover:bg-white/10 lg:justify-center xl:justify-start"
            >
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-[13px] font-extrabold text-yellow">
                {initials(user.fullName)}
              </div>
              <div className="min-w-0 lg:hidden xl:block">
                <div className="truncate text-[13px] font-bold leading-tight text-white">
                  {user.fullName}
                </div>
                <div className="text-xs font-semibold text-brand-200">
                  {ROLE_LABEL[user.role] ?? user.role}
                </div>
              </div>
            </Link>
            <div className="mt-2 px-2.5 text-xs font-semibold text-white/35 lg:hidden xl:block">
              versiya {APP_VERSION}
              {/* NF-309: dev/demo muhitida ma'lumot sintetik ekani ko'rinib turadi. */}
              <div className="mt-0.5">Ma'lumot sintetik</div>
            </div>
          </div>
        </aside>

        {/* Mobil qoplama */}
        {menuOpen ? (
          <button
            type="button"
            aria-label="Menyuni yopish"
            onClick={() => {
              setMenuOpen(false);
            }}
            className="fixed inset-0 z-30 bg-navy/40 lg:hidden"
          />
        ) : null}

        {/* ── Asosiy ustun — endi `pl-[...]` bilan emas, flex qatorda yon
            panel YONIDA turadi ── */}
        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-20 flex h-16 items-center gap-3 bg-canvas/85 px-4 backdrop-blur sm:px-6 lg:top-3.5 lg:rounded-panel lg:px-6">
            <button
              type="button"
              aria-label="Menyu"
              onClick={() => {
                setMenuOpen(true);
              }}
              className="-ml-1 flex size-11 items-center justify-center rounded-tile text-body hover:bg-brand-50 lg:hidden"
            >
              <Menu className="size-5" strokeWidth={1.85} aria-hidden="true" />
            </button>

            {user.businesses.length > 1 ? (
              <select
                aria-label="Biznes"
                value={user.businessId}
                onChange={(event) => void switchBusiness(event.target.value)}
                className="h-11 max-w-[240px] rounded-tile border border-border bg-surface px-3 text-[15px] text-ink focus:border-brand-600 focus:outline-none focus:ring-4 focus:ring-brand-600/25"
              >
                {user.businesses.map((business) => (
                  <option key={business.id} value={business.id}>
                    {business.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="truncate text-[15px] font-bold text-ink">{user.businessName}</span>
            )}

            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <NotificationBell />
              <button
                type="button"
                onClick={logout}
                className="flex h-[38px] items-center gap-1.5 rounded-full border border-border bg-surface px-4 text-[13px] font-semibold text-navy transition-colors hover:border-brand-100 hover:bg-brand-50"
              >
                <LogOut className="size-4" strokeWidth={1.85} aria-hidden="true" />
                <span className="hidden sm:inline">Chiqish</span>
              </button>
            </div>
          </header>

          {/* PDP rule ekran boshiga BIR marta chiziladi — u `PageHeader`
              ichida (sarlavha ostida). Shu sabab bu yerda takrorlanmaydi. */}
          <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6">{children}</main>
        </div>
      </div>
    </SessionProvider>
  );
}

/** Ism-familiyadan ikki harf (avatar o'rniga). */
function initials(fullName: string): string {
  return fullName
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * Har marshrutni qobiqqa o'raydi.
 *
 * `Suspense` QOBIQ ICHIDA: shunda sahifa yuklanayotganda yon panel va
 * yuqori qator joyida qoladi va interfeys «sakramaydi».
 */
function shell(element: JSX.Element): JSX.Element {
  return (
    <Shell>
      {/* Skeleton — sahifa tuzilishini oldindan ko'rsatadi, «sakrash» yo'q. */}
      <Suspense
        fallback={
          <div className="space-y-5" aria-label="Yuklanmoqda">
            <Skeleton className="h-24 w-full" />
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} className="h-28" />
              ))}
            </div>
            <Skeleton className="h-72 w-full" />
          </div>
        }
      >
        {element}
      </Suspense>
    </Shell>
  );
}

export default function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* PP-1203 — taklifni qabul qilish: qobiqdan TASHQARIDA, authsiz. */}
        <Route
          path="/accept-invite"
          element={
            <Suspense fallback={<BootScreen />}>
              <AcceptInvitePage />
            </Suspense>
          }
        />
        {/* PP-412 — ulashilgan ko'rinish: qobiqdan TASHQARIDA, authsiz. */}
        <Route
          path="/shared/:token"
          element={
            <Suspense fallback={<BootScreen />}>
              <SharedCallPage />
            </Suspense>
          }
        />
        {/* NF-505 — status: qobiqdan TASHQARIDA, authsiz. */}
        <Route
          path="/status"
          element={
            <Suspense fallback={<BootScreen />}>
              <StatusPage />
            </Suspense>
          }
        />
        <Route path="/" element={shell(<DashboardPage />)} />
        <Route path="/tasks" element={shell(<TasksPage />)} />
        <Route path="/my" element={shell(<MyPage />)} />
        <Route path="/calls" element={shell(<CallsPage />)} />
        <Route path="/calls/:id" element={shell(<CallPage />)} />
        <Route path="/analytics" element={shell(<AnalyticsPage />)} />
        <Route path="/leads" element={shell(<LeadsPage />)} />
        <Route path="/leads/:id" element={shell(<LeadDetailPage />)} />
        <Route path="/assistant" element={shell(<AssistantPage />)} />
        <Route path="/signals" element={shell(<SignalsPage />)} />
        <Route path="/team" element={shell(<TeamPage />)} />
        <Route path="/reports" element={shell(<ReportsPage />)} />
        <Route path="/notifications" element={shell(<NotificationsPage />)} />
        <Route path="/profile" element={shell(<ProfilePage />)} />
        <Route path="/settings" element={shell(<SettingsPage />)} />
        {/* Noma'lum manzil — bosh sahifaga. 404 sahifasi B2 ishi. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
